import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Link,
  useTheme,
  DialogContentText,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import LinkIcon from '@mui/icons-material/Link';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { useProject } from '../contexts/ProjectContext';
import { getProjectContents, deleteContent, uploadContent } from '../services/api';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Joyride, { STATUS } from 'react-joyride';

const ContentPreviewDialog = ({ open, onClose, content, onDelete }) => {
  const theme = useTheme();
  
  const handleCopyContent = () => {
    navigator.clipboard.writeText(content.content);
  };

  const handleDelete = () => {
    onDelete(content);
    onClose();
  };

  if (!content) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          bgcolor: 'grey.900',
          color: 'common.white',
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'grey.900' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'common.white' }}>
            {content.title}
          </Typography>
          <Chip
            icon={content.source_type === 'url' ? <LinkIcon /> : <UploadFileIcon />}
            label={content.source_type === 'url' ? 'URL' : 'Upload'}
            size="small"
            color={content.source_type === 'url' ? 'primary' : 'secondary'}
          />
        </Box>
      </DialogTitle>
      <Divider sx={{ borderColor: 'grey.800' }} />
      <DialogContent sx={{ bgcolor: 'grey.900' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="grey.400" gutterBottom>
            Source
          </Typography>
          {content.source_type === 'url' ? (
            <Link 
              href={content.source} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ color: theme.palette.primary.light }}
            >
              {content.source}
            </Link>
          ) : (
            <Typography variant="body2" color="common.white">{content.source}</Typography>
          )}
        </Box>
        <Divider sx={{ my: 2, borderColor: 'grey.800' }} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="grey.400" gutterBottom>
            Content
          </Typography>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              maxHeight: '400px', 
              overflow: 'auto',
              bgcolor: 'grey.800',
              borderColor: 'grey.700',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'grey.800',
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'grey.700',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                bgcolor: 'grey.600',
              },
            }}
          >
            <Typography 
              variant="body2" 
              component="pre" 
              sx={{ 
                whiteSpace: 'pre-wrap', 
                fontFamily: 'inherit',
                color: 'common.white',
              }}
            >
              {content.content}
            </Typography>
          </Paper>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="grey.500">
            Created {new Date(content.created_at).toLocaleString()}
            {content.updated_at !== content.created_at && 
              ` • Updated ${new Date(content.updated_at).toLocaleString()}`}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'grey.900', borderTop: 1, borderColor: 'grey.800' }}>
        <Button 
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
          color="error"
          sx={{ color: theme.palette.error.light }}
        >
          Delete
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button 
          startIcon={<ContentCopyIcon />}
          onClick={handleCopyContent}
          sx={{ color: 'grey.300' }}
        >
          Copy Content
        </Button>
        <Button 
          onClick={onClose}
          sx={{ color: 'grey.300' }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const UploadZone = ({ onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const { selectedProject } = useProject();
  const { enqueueSnackbar } = useSnackbar();

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!selectedProject) return;

    setUploading(true);
    try {
      for (const file of acceptedFiles) {
        await uploadContent(file, selectedProject.id);
      }
      enqueueSnackbar('Files uploaded successfully', { variant: 'success' });
      onUploadComplete();
    } catch (error) {
      console.error('Upload error:', error);
      enqueueSnackbar(error.response?.data?.detail || 'Upload failed', { 
        variant: 'error' 
      });
    } finally {
      setUploading(false);
    }
  }, [selectedProject, onUploadComplete, enqueueSnackbar]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'application/rtf': ['.rtf'],
      'text/markdown': ['.md'],
      'application/vnd.oasis.opendocument.text': ['.odt'],
      'application/epub+zip': ['.epub'],
      'text/html': ['.html', '.htm'],
      'application/xhtml+xml': ['.xhtml'],
      'message/rfc822': ['.mht', '.mhtml'],
      'text/xml': ['.xml'],
      'application/xml': ['.xml'],
      'text/csv': ['.csv'],
      'application/json': ['.json']
    },
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  return (
    <Paper
      {...getRootProps()}
      className="upload-zone"
      sx={{
        p: 3,
        mb: 3,
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'grey.500',
        bgcolor: isDragActive ? 'action.hover' : 'background.paper',
        cursor: 'pointer',
        opacity: uploading ? 0.7 : 1,
        transition: 'all 0.2s ease',
      }}
    >
      <input {...getInputProps()} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Typography variant="body1" align="center">
          {isDragActive
            ? 'Drop the files here...'
            : 'Drag & drop files here, or click to select files'}
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center">
          Supported formats: PDF, DOCX, DOC, RTF, ODT, EPUB, HTML, XHTML, MHTML, TXT, MD, XML, CSV, JSON (Max 10MB)
        </Typography>
        {uploading && <LinearProgress sx={{ width: '100%', mt: 1 }} />}
      </Box>
    </Paper>
  );
};

const Resources = () => {
  const { selectedProject } = useProject();
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedContent, setSelectedContent] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    loadContents();
  }, [selectedProject, page, rowsPerPage]);

  const loadContents = async () => {
    if (!selectedProject) return;
    
    try {
      setLoading(true);
      const data = await getProjectContents(selectedProject.id, page + 1, rowsPerPage);
      setContents(data.items);
      setTotalCount(data.total);
      setError(null);
    } catch (err) {
      setError('Failed to load content resources');
      console.error('Error loading contents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (content) => {
    setSelectedContent(content);
  };

  const handleDelete = (content) => {
    setContentToDelete(content);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteContent(contentToDelete.id);
      enqueueSnackbar('Content deleted successfully', { variant: 'success' });
      setDeleteConfirmOpen(false);
      setContentToDelete(null);
      loadContents(); // Reload the content list
      
      // If the deleted content was being previewed, close the preview
      if (selectedContent?.id === contentToDelete.id) {
        setSelectedContent(null);
      }
    } catch (error) {
      console.error('Error deleting content:', error);
      enqueueSnackbar(error.response?.data?.detail || 'Failed to delete content', { 
        variant: 'error' 
      });
    }
  };

  const DeleteConfirmationDialog = () => (
    <Dialog
      open={deleteConfirmOpen}
      onClose={() => setDeleteConfirmOpen(false)}
      PaperProps={{
        sx: {
          bgcolor: 'grey.900',
          color: 'common.white',
        }
      }}
    >
      <DialogTitle>Confirm Deletion</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: 'grey.300' }}>
          Are you sure you want to delete "{contentToDelete?.title}"? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => setDeleteConfirmOpen(false)}
          sx={{ color: 'grey.300' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={confirmDelete} 
          color="error"
          variant="contained"
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (!selectedProject) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">
          Please select a project to view resources
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Resources
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Resources are documents and web pages that you can use as reference material for your content generation. You can upload various document formats including PDF, Word documents (DOCX, DOC), OpenDocument (ODT), Rich Text (RTF), eBooks (EPUB), web pages (HTML, XHTML, MHTML), and text files (TXT, MD, XML, CSV, JSON). These resources will be used to provide context and information when generating articles, ensuring your content is well-researched and accurate.
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 3, p: 2, bgcolor: 'grey.900', borderRadius: 1, border: 1, borderColor: 'grey.700' }}>
        ℹ️ Please note: To ensure proper usage, only upload documents that you own, have permission to use, or are publicly available. For more information about content usage, please review our <Link href="https://writellect.com/terms" sx={{ color: 'primary.main' }}>Terms of Service</Link>.
      </Typography>
      
      <UploadZone onUploadComplete={loadContents} />
      
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer className="resources-table" sx={{ maxHeight: 'calc(100vh - 250px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell className="source-type-column">Source Type</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell className="actions-column" align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contents.map((content) => (
                <TableRow 
                  hover 
                  key={content.id}
                  onClick={() => handleRowClick(content)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {content.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={content.source_type === 'url' ? <LinkIcon /> : <UploadFileIcon />}
                      label={content.source_type === 'url' ? 'URL' : 'Upload'}
                      size="small"
                      color={content.source_type === 'url' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    {content.source_type === 'url' ? (
                      <Tooltip title={content.source}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(content.source, '_blank');
                          }}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {content.source}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={new Date(content.created_at).toLocaleString()}>
                      <Typography variant="body2" color="textSecondary">
                        {formatDistanceToNow(new Date(content.created_at), { addSuffix: true })}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={new Date(content.updated_at).toLocaleString()}>
                      <Typography variant="body2" color="textSecondary">
                        {formatDistanceToNow(new Date(content.updated_at), { addSuffix: true })}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(content);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {contents.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No content resources found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          className="table-pagination"
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <ContentPreviewDialog
        open={!!selectedContent}
        onClose={() => setSelectedContent(null)}
        content={selectedContent}
        onDelete={handleDelete}
      />
      
      <DeleteConfirmationDialog />
    </Box>
  );
};

export default Resources; 